import React, { useState, useEffect } from 'react';
import { schoolClassAPI } from '../../services/api';
import './MasterDataManagement.css';

const SchoolClassManagement = () => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingClass, setEditingClass] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [newClass, setNewClass] = useState({
    id: 0,
    school: '',
    class: '',
    description: '',
    status: 'Active'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [schoolFilter, setSchoolFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    loadClasses();
  }, [searchTerm, schoolFilter, currentPage, pageSize]);

  const loadClasses = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await schoolClassAPI.getSchoolClasses(schoolFilter, searchTerm);
      
      if (response.success) {
        setClasses(response.data);
        setTotalCount(response.data.length);
        setTotalPages(Math.ceil(response.data.length / pageSize));
      } else {
        setError(response.message || 'Failed to load school classes');
      }
    } catch (err) {
      console.error('Load school classes error:', err);
      setError('An error occurred while loading school classes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClass = () => {
    setModalMode('create');
    setEditingClass(null);
    setNewClass({
      id: 0,
      school: '',
      class: '',
      description: '',
      status: 'Active'
    });
    setShowModal(true);
  };

  const handleEditClass = (schoolClass) => {
    setModalMode('edit');
    setEditingClass(schoolClass);
    setNewClass({
      id: schoolClass.id,
      school: schoolClass.school || '',
      class: schoolClass.class || '',
      description: schoolClass.description || '',
      status: schoolClass.status || 'Active'
    });
    setShowModal(true);
  };

  const handleDeleteClass = async (id) => {
    if (window.confirm(`Are you sure you want to delete school class with ID ${id}? This will check if any students are enrolled in this class.`)) {
      try {
        const response = await schoolClassAPI.deleteSchoolClass(id);
        
        if (response.success) {
          loadClasses();
          console.log('School class deleted successfully');
        } else {
          setError(response.message || 'Failed to delete school class');
        }
      } catch (err) {
        console.error('Delete error:', err);
        setError('An error occurred while deleting the school class');
      }
    }
  };

  const handleSaveClass = async () => {
    try {
      let response;
      
      if (modalMode === 'create') {
        response = await schoolClassAPI.createClass({
          school: newClass.school,
          class: newClass.class,
          description: newClass.description,
          status: newClass.status
        });
      } else {
        response = await schoolClassAPI.updateClass(editingClass.id, {
          id: newClass.id,
          school: newClass.school,
          class: newClass.class,
          description: newClass.description,
          status: newClass.status
        });
      }

      if (response.success) {
        setShowModal(false);
        loadClasses();
        console.log(`${modalMode === 'create' ? 'Created' : 'Updated'} school class successfully`);
      } else {
        setError(response.message || `Failed to ${modalMode} school class`);
      }
    } catch (err) {
      console.error('Save error:', err);
      setError(`An error occurred while ${modalMode === 'create' ? 'creating' : 'updating'} the school class`);
    }
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  if (loading && !classes.length) {
    return (
      <div className="master-data-management">
        <div className="loading">Loading school classes...</div>
      </div>
    );
  }

  return (
    <div className="master-data-management">
      <div className="master-data-management-header">
        <div className="header-info">
          <h2><i className="fas fa-chalkboard-teacher"></i> School Class Management</h2>
          <p>Manage class levels within schools (e.g., 1A, 1B, 2A, etc.)</p>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="master-data-management-toolbar">
        <div className="search-section">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search classes..."
            className="search-input"
          />
          <select
            value={schoolFilter}
            onChange={(e) => setSchoolFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Schools</option>
            {/* This would typically be populated from an API call */}
            {/* For now, using placeholder */}
          </select>
          <button onClick={loadClasses} className="btn-search">
            <i className="fas fa-search"></i> Filter
          </button>
        </div>
        <button onClick={handleCreateClass} className="btn-create">
          <i className="fas fa-plus"></i> Add New Class
        </button>
      </div>

      <div className="table-container">
        <table className="master-data-table">
          <thead>
            <tr>
              <th>No.</th>
              <th>School Code</th>
              <th>Class Name</th>
              <th>Description</th>
              <th>Status</th>
              <th>Created Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {classes.length > 0 ? (
              classes.map((schoolClass, index) => (
                <tr key={schoolClass.id}>
                  <td>{index + 1}</td>
                  <td><strong>{schoolClass.school}</strong></td>
                  <td>{schoolClass.class}</td>
                  <td>{schoolClass.description || '-'}</td>
                  <td>
                    <span className={`badge ${schoolClass.status === 'Active' ? 'badge-success' : 'badge-secondary'}`}>
                      {schoolClass.status}
                    </span>
                  </td>
                  <td>
                    {schoolClass.createdDate ? new Date(schoolClass.createdDate).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="actions-column">
                    <button
                      onClick={() => handleEditClass(schoolClass)}
                      className="btn-action btn-edit"
                      title="Edit Class"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      onClick={() => handleDeleteClass(schoolClass.id)}
                      className="btn-action btn-delete"
                      title="Delete Class"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="no-data">No school classes found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination-section">
          <div className="pagination-info">
            Page {currentPage} of {totalPages} ({totalCount} total classes)
          </div>
          <div className="pagination-controls">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="btn-page"
            >
              First
            </button>
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="btn-page"
            >
              Previous
            </button>
            <span className="page-input">
              <input
                type="number"
                value={currentPage}
                onChange={(e) => setCurrentPage(Math.max(1, Math.min(totalPages, Number(e.target.value))))}
                min="1"
                max={totalPages}
                className="page-number-input"
              />
              <span>of {totalPages}</span>
            </span>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="btn-page"
            >
              Next
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="btn-page"
            >
              Last
            </button>
          </div>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(parseInt(e.target.value));
              setCurrentPage(1);
            }}
            className="page-size-select"
          >
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </select>
        </div>
      )}

      {/* Modal for Add/Edit Class */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>
                {modalMode === 'create' ? 'Add New School Class' : `Edit Class: ${newClass.class}`}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="modal-close-btn"
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="schoolCode">School Code *</label>
                <input
                  id="schoolCode"
                  type="text"
                  value={newClass.school}
                  onChange={(e) => setNewClass({ ...newClass, school: e.target.value.trim().toUpperCase() })}
                  placeholder="Enter school code (e.g., XBA1001)"
                  maxLength={50}
                  className="form-control"
                  disabled={modalMode === 'edit'} // Don't allow changing school after creation
                />
                <small className="form-help">School code where this class belongs (cannot be changed after creation)</small>
              </div>

              <div className="form-group">
                <label htmlFor="className">Class Name *</label>
                <input
                  id="className"
                  type="text"
                  value={newClass.class}
                  onChange={(e) => setNewClass({ ...newClass, class: e.target.value.trim().toUpperCase() })}
                  placeholder="Enter class name (e.g., 1A, 1B, 6PINTAR)"
                  maxLength={50}
                  className="form-control"
                />
                <small className="form-help">Class identifier (e.g., 1A, 1B, 6PINTAR)</small>
              </div>

              <div className="form-group">
                <label htmlFor="classDescription">Description</label>
                <input
                  id="classDescription"
                  type="text"
                  value={newClass.description}
                  onChange={(e) => setNewClass({ ...newClass, description: e.target.value.trim() })}
                  placeholder="Enter class description (optional)"
                  maxLength={255}
                  className="form-control"
                />
                <small className="form-help">Additional description for the class</small>
              </div>

              <div className="form-group">
                <label htmlFor="classStatus">Status *</label>
                <select
                  id="classStatus"
                  value={newClass.status}
                  onChange={(e) => setNewClass({ ...newClass, status: e.target.value })}
                  className="form-control"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
                <small className="form-help">Class availability status</small>
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowModal(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveClass}
                disabled={!newClass.school.trim() || !newClass.class.trim()}
                className="btn btn-primary"
              >
                {modalMode === 'create' ? 'Create Class' : 'Update Class'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchoolClassManagement;